import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SubPageHeader } from "@/components/SubPageHeader";
import { Chip } from "@/components/Chip";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Plus, X, Check, X as XIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, todayISO } from "@/lib/date";

const TYPES = ["casual", "sick", "earned", "comp"];

export default function LeaveManager() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", date: todayISO(), type: "casual", reason: "" });

  const load = async () => {
    try {
      const [l, u] = await Promise.all([api.get("/leaves"), api.get("/users")]);
      setLeaves(l.data.sort((a, b) => b.applied_at.localeCompare(a.applied_at)));
      setUsers(u.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/leaves", { ...form, user_id: isHR ? (form.user_id || user.user_id) : undefined });
      toast.success("Leave submitted");
      setOpen(false); setForm({ user_id: "", date: todayISO(), type: "casual", reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/leaves/${id}`, { status }); toast.success(status); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete leave?")) return;
    try { await api.delete(`/leaves/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const isAdmin = user?.role === "admin";
  const isHR = user?.role === "admin" || user?.role === "hr_manager";

  return (
    <div className="min-h-[100svh]" data-testid="leaves-page" style={{ background: "#0A0D14", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <SubPageHeader title="Leave Manager" right={
        <button onClick={() => setOpen(true)} data-testid="apply-leave-button" className="h-9 px-3 rounded-lg flex items-center gap-1 text-xs font-bold" style={{ background: "#4F8EF7", color: "#fff" }}>
          <Plus size={14} /> Apply
        </button>
      } />
      <div className="px-4 py-4 space-y-2">
        {leaves.length === 0 && <div className="text-center text-sm py-8" style={{ color: "#7A85A3" }}>No leaves yet</div>}
        {leaves.map((l) => (
          <div key={l.id} className="rounded-2xl p-4" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={`leave-${l.id}`}>
            <div className="flex items-start gap-2 mb-1">
              <div className="flex-1 min-w-0">
                {isHR && <div className="text-sm font-bold truncate" style={{ color: "#E8ECFF" }}>{l.user_name}</div>}
                <div className="text-xs" style={{ color: "#7A85A3" }}>{fmtDate(l.date, "EEE, d MMM yyyy")}</div>
              </div>
              <Chip color={l.status === "approved" ? "green" : l.status === "rejected" ? "red" : "amber"}>{l.status}</Chip>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Chip color="blue">{l.type}</Chip>
            </div>
            {l.reason && <div className="text-xs" style={{ color: "#7A85A3" }}>{l.reason}</div>}
            {isHR && l.status === "pending" && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => setStatus(l.id, "approved")} data-testid={`approve-${l.id}`} className="flex-1 h-9 rounded-lg flex items-center justify-center gap-1 text-xs font-bold" style={{ background: "rgba(34,211,165,0.15)", color: "#22D3A5", border: "1px solid rgba(34,211,165,0.3)" }}>
                  <Check size={14} /> Approve
                </button>
                <button onClick={() => setStatus(l.id, "rejected")} data-testid={`reject-${l.id}`} className="flex-1 h-9 rounded-lg flex items-center justify-center gap-1 text-xs font-bold" style={{ background: "rgba(247,90,90,0.1)", color: "#F75A5A", border: "1px solid rgba(247,90,90,0.3)" }}>
                  <XIcon size={14} /> Reject
                </button>
              </div>
            )}
            {isAdmin && (
              <button onClick={() => remove(l.id)} data-testid={`delete-leave-${l.id}`} className="mt-2 text-xs flex items-center gap-1" style={{ color: "#F75A5A" }}>
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>
        ))}
      </div>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent style={{ background: "#111520", borderColor: "#1E2640" }}>
          <div className="mx-auto w-12 h-1.5 rounded-full mt-2 mb-3" style={{ background: "#1E2640" }} />
          <div className="px-6 pb-8 space-y-3" data-testid="apply-leave-form">
            <div className="flex items-center justify-between mb-2">
              <div className="font-syne text-xl font-bold tracking-tight" style={{ color: "#E8ECFF" }}>Apply for Leave</div>
              <button onClick={() => setOpen(false)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#7A85A3" }}><X size={16} /></button>
            </div>
            {isHR && (
              <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} data-testid="leave-user"
                className="w-full h-11 px-3 rounded-xl text-sm" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }}>
                <option value="">Self ({user.name})</option>
                {users.filter((u) => u.user_id !== user.user_id).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            )}
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="leave-date"
              className="w-full h-11 px-3 rounded-xl" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="leave-type"
              className="w-full h-11 px-3 rounded-xl text-sm" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason" rows={3} data-testid="leave-reason"
              className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            <button onClick={submit} data-testid="leave-submit" className="w-full h-12 rounded-xl font-bold" style={{ background: "#4F8EF7", color: "#fff" }}>Submit</button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
